import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Save, ShieldCheck, UserCog } from 'lucide-react';
import { useAuthStore } from '@/auth';
import { clinicianHomeHospitalId } from '@/api/clinicianHospital';
import { useCurrentClinician, useUpdateClinician } from '@/hooks/queries';
import { LANGUAGES, resolveLanguageCode, type LanguageCode } from '@/i18n/languages';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { DisplayPreferencesCard } from '@/components/DisplayPreferencesCard';
import { extractApiErrorMessage } from '@/lib/apiError';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';

/**
 * Clinician self-service profile management. Reachable from the sidebar
 * footer in `PageLayout`. Mirrors v1's `addClinician` form fields but
 * scoped to the LOGGED-IN clinician — they can only edit themselves
 * here, not other clinicians.
 *
 * Two cards:
 *   • Profile  — first/last name, email, phone, bio. Saved via PATCH.
 *   • Security — change password. Separate so the user's name/email
 *     edits don't have to round-trip the password field.
 *
 * Locale is still managed by the global `LanguageSwitcher` in the header,
 * not duplicated here — that switcher already PATCHes the clinician
 * locale, and surfacing two controls for the same field would split the
 * UX. The Profile card just shows the current locale read-only as a hint.
 */
type ProfileForm = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
  bio: string;
};

const blankForm = (): ProfileForm => ({
  first_name: '',
  last_name: '',
  email: '',
  phone_number: '',
  bio: '',
});

export default function Profile() {
  const { t } = useTranslation();
  const clinicianQuery = useCurrentClinician();
  const updateClinician = useUpdateClinician();
  const authClinician = useAuthStore((s) => s.clinician);

  // Seed the form from the server response; track a separate "initial"
  // snapshot so we can detect dirty state without diffing against the
  // mutable form object directly.
  const [form, setForm] = useState<ProfileForm>(blankForm);
  const [initial, setInitial] = useState<ProfileForm>(blankForm);
  // Password fields live in their own state object so editing them doesn't
  // mark the main profile form dirty.
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const c = clinicianQuery.data;
    if (!c) return;
    const next: ProfileForm = {
      first_name: c.first_name ?? '',
      last_name: c.last_name ?? '',
      email: c.email ?? '',
      phone_number: c.phone_number ?? '',
      bio: c.bio ?? '',
    };
    setForm(next);
    setInitial(next);
  }, [clinicianQuery.data]);

  const profileDirty = useMemo(
    () =>
      form.first_name !== initial.first_name ||
      form.last_name !== initial.last_name ||
      form.email !== initial.email ||
      form.phone_number !== initial.phone_number ||
      form.bio !== initial.bio,
    [form, initial],
  );

  const passwordDirty = newPassword.length > 0 || confirmPassword.length > 0;
  const passwordError =
    passwordDirty && newPassword !== confirmPassword
      ? t('Passwords do not match.')
      : passwordDirty && newPassword.length < 8
        ? t('Password must be at least 8 characters.')
        : '';

  // The auth store's Clinician type has an `[k: string]: unknown` index
  // signature (it's a wire-format slim mirror), so hospital id is narrowed
  // via clinicianHomeHospitalId.
  const c = clinicianQuery.data;
  const hospitalId: number | undefined =
    clinicianHomeHospitalId(c) ?? clinicianHomeHospitalId(authClinician);
  const clinicianId: number | undefined =
    c?.id ?? (typeof authClinician?.id === 'number' ? authClinician.id : undefined);

  const onSaveProfile = async () => {
    if (!hospitalId || !clinicianId || !profileDirty) return;
    try {
      // Only send fields the user actually changed — PATCH semantics on the
      // server mean omitted fields keep their prior value, but sending only
      // diffs also helps audit logs read cleanly.
      const body: Record<string, string | null> = {};
      if (form.first_name !== initial.first_name) body.first_name = form.first_name.trim();
      if (form.last_name !== initial.last_name) body.last_name = form.last_name.trim();
      if (form.email !== initial.email) body.email = form.email.trim();
      if (form.phone_number !== initial.phone_number)
        body.phone_number = form.phone_number.trim();
      if (form.bio !== initial.bio) body.bio = form.bio;
      await updateClinician.mutateAsync({ hospitalId, clinicianId, body });
      toast({ title: t('Profile updated') });
      // Only forward which field-categories changed — never the values.
      track(ANALYTICS_EVENTS.CLINICIAN_PROFILE_EDITED, {
        changed_fields: Object.keys(body).join(','),
      });
    } catch (err) {
      toast({
        title: t('Could not update profile'),
        description: extractApiErrorMessage(err),
        variant: 'destructive',
      });
    }
  };

  const onChangePassword = async () => {
    if (!hospitalId || !clinicianId || !passwordDirty || passwordError) return;
    try {
      await updateClinician.mutateAsync({
        hospitalId,
        clinicianId,
        body: { password: newPassword },
      });
      setNewPassword('');
      setConfirmPassword('');
      toast({ title: t('Password updated') });
    } catch (err) {
      toast({
        title: t('Could not update password'),
        description: extractApiErrorMessage(err),
        variant: 'destructive',
      });
    }
  };

  if (clinicianQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <header className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <UserCog className="h-6 w-6 text-primary" />
          {t('My Profile')}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('Update your name, contact details, and bio. Your clinic admin manages roles and permissions.')}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('Profile')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <FieldRow label={t('First name')}>
              <Input
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                autoComplete="given-name"
              />
            </FieldRow>
            <FieldRow label={t('Last name')}>
              <Input
                value={form.last_name}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                autoComplete="family-name"
              />
            </FieldRow>
          </div>

          <FieldRow
            label={t('Email')}
            hint={
              c?.email_verified ? (
                <span className="inline-flex items-center gap-1 text-emerald-700">
                  <ShieldCheck className="h-3 w-3" />
                  {t('Verified')}
                </span>
              ) : (
                <span className="text-amber-700">{t('Not verified')}</span>
              )
            }
          >
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              autoComplete="email"
            />
          </FieldRow>

          <FieldRow label={t('Phone number')}>
            <Input
              type="tel"
              value={form.phone_number}
              onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
              autoComplete="tel"
            />
          </FieldRow>

          <FieldRow label={t('Bio')}>
            <textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </FieldRow>

          {/* Locale change applies immediately (i18n + optimistic auth-store
              update + PATCH) — same flow as the header LanguageSwitcher, so
              switching here keeps both surfaces in sync. The "default
              language on all pages" wiring lives in `useUserDefaultLanguage`,
              which reads `clinician.locale` once on PageLayout mount. */}
          <FieldRow label={t('Language')}>
            <LocaleSelect />
          </FieldRow>

          <div className="flex flex-wrap items-center gap-2 border-t pt-3 text-xs text-muted-foreground">
            <Mail className="h-3 w-3" />
            <a
              href={`mailto:${c?.email ?? ''}`}
              className="text-primary hover:underline"
            >
              {c?.email}
            </a>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={onSaveProfile}
              disabled={!profileDirty || updateClinician.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {t('Save changes')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('Change password')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FieldRow label={t('New password')}>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </FieldRow>
          <FieldRow label={t('Confirm new password')}>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </FieldRow>
          {passwordError && (
            <p className="text-xs text-destructive">{passwordError}</p>
          )}
          <div className="flex justify-end">
            <Button
              onClick={onChangePassword}
              disabled={!passwordDirty || !!passwordError || updateClinician.isPending}
            >
              {t('Update password')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <DisplayPreferencesCard />
    </div>
  );
}

const FieldRow = ({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <div className="space-y-1">
    <div className="flex items-baseline justify-between">
      <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </Label>
      {hint && <span className="text-[0.6875rem]">{hint}</span>}
    </div>
    {children}
  </div>
);

/**
 * Native select wired into the same i18n + PATCH flow the header
 * `LanguageSwitcher` uses. Changes apply immediately (no separate save
 * button) — i18next switches locally, the auth-store gets an optimistic
 * update so `useUserDefaultLanguage` doesn't bounce the language back
 * before the PATCH resolves, then the PATCH writes the canonical value.
 */
const LocaleSelect = () => {
  const { i18n, t } = useTranslation();
  const clinician = useAuthStore((s) => s.clinician);
  const setClinician = useAuthStore((s) => s.setClinician);
  const updateClinician = useUpdateClinician();
  const current = resolveLanguageCode(i18n.language);

  const onChange = (next: LanguageCode) => {
    if (next === current) return;
    i18n.changeLanguage(next);
    const hospitalId = clinicianHomeHospitalId(clinician);
    if (clinician?.id && hospitalId != null) {
      setClinician({ ...clinician, locale: next });
      updateClinician.mutate({
        hospitalId,
        clinicianId: clinician.id,
        body: { locale: next },
      });
    }
  };

  return (
    <select
      value={current}
      onChange={(e) => onChange(e.target.value as LanguageCode)}
      className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={t('Language')}
    >
      {LANGUAGES.map((lang) => (
        <option key={lang.code} value={lang.code}>
          {lang.label}
        </option>
      ))}
    </select>
  );
};
